import React from 'react';
import PropTypes from 'prop-types';
import Heading from '../Heading';
import { st, classes } from './EditableTitle.st.css';
import { dataHooks } from './constants';
import { WixDesignSystemContext } from '../WixDesignSystemProvider/context';

const DEFAULT_MAX_LENGTH = 100;

class EditableTitle extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      focus: false,
      value: props.initialValue || '',
    };
  }

  wsrInput = React.createRef();

  componentDidMount() {
    if (this.props.autoFocus) this.onFocus();
  }

  onChange = e => {
    this.setState({ value: e.target.value });
  };

  showPlaceholder = () => !this.state.value && this.props.defaultValue;

  onFocus = () => {
    const value = this.state.value || this.props.defaultValue;
    const selectAll = !this.state.focus;

    this.setState({ focus: true, value }, () => {
      this.wsrInput.current.focus();
      selectAll && this.wsrInput.current.select();
    });
  };

  onKeyDown = e => {
    if (e.key === 'Enter' || e.keyCode === 13) {
      this.onEnterPressed();
    }
  };

  render() {
    const { dataHook, className, maxLength } = this.props;
    const focused = this.state.focus;

    return (
      <WixDesignSystemContext.Consumer>
        {({ newColorsBranding }) => (
          <div
            className={st(classes.root, { newColorsBranding }, className)}
            data-hook={dataHook}
            tabIndex={0}
            onFocus={this.onFocus}
            onClick={this.onFocus}
          >
            <div
              data-hook={dataHooks.heading}
              className={st(classes.headingWrapper, {
                focused,
                showPlaceholder: !!this.showPlaceholder(),
              })}
            >
              <Heading ellipsis size="extraLarge" className={classes.heading}>
                {this.props.value ||
                  this.state.value ||
                  this.props.defaultValue}
              </Heading>
            </div>
            <div
              className={st(classes.activationIndicator, {
                focused,
                newColorsBranding,
              })}
            >
              {this.props.value || this.state.value || this.props.defaultValue}
            </div>
            <div
              className={st(classes.renamingField, {
                focused,
              })}
              data-hook={dataHooks.renamingField}
              onFocus={e =>
                // input does not pass his event so we need to catch it
                e.stopPropagation()
              }
            >
              <input
                className={classes.input}
                data-hook={dataHooks.input}
                onBlur={this.onValueSubmission}
                ref={this.wsrInput}
                value={this.props.value ? this.props.value : this.state.value}
                maxLength={maxLength || DEFAULT_MAX_LENGTH}
                onChange={
                  this.props.onChange ? this.props.onChange : this.onChange
                }
                onKeyDown={this.onKeyDown}
              />
            </div>
          </div>
        )}
      </WixDesignSystemContext.Consumer>
    );
  }

  onEnterPressed = () => {
    this.wsrInput.current.blur();
  };

  onValueSubmission = () => {
    const value = this.state.value || this.props.defaultValue;
    this.setState({ value, focus: false }, () => {
      if (typeof this.props.onSubmit === 'function') {
        this.props.onSubmit(value);
      }
    });
  };
}

EditableTitle.displayName = 'EditableTitle';

EditableTitle.defaultProps = {
  defaultValue: '',
};

EditableTitle.propTypes = {
  /** Defines a text value that is shown initially */
  initialValue: PropTypes.string,
  /** Defines text value that is shown when the title is empty. When clicked this value will become a default 'value' */
  defaultValue: PropTypes.string,
  /** Triggers function when a user is done with editing the title */
  onSubmit: PropTypes.func,
  /** Sets the max number of characters that a user can type in the title */
  maxLength: PropTypes.number,
  /** Sets focus on the title as soon as the component is rendered (on mount)  */
  autoFocus: PropTypes.bool,
  /** Triggers function when the title is being edited */
  onChange: PropTypes.func,
  /** Sets the controlled value of the input */
  value: PropTypes.string,
};

export default EditableTitle;
